<script setup lang="ts">
import { computed } from 'vue'

import { SportId } from '@collector/sportsapi-client-legacy'
import IncidentsToConfirm from '@desktop/views/Relation/components/RelationView/Main/IncidentsToConfirm/IncidentsToConfirm.vue'
import { useIncidentsToConfirm } from '@desktop/views/Relation/components/RelationView/Main/IncidentsToConfirm/useIncidentsToConfirm'
import { useEvent, useTeamSides } from '@desktop/views/Relation/composables'
import HandyInfoBar from '@desktop/views/Relation/Sports/components/RelationView/Main/PitchWithIncidents/HandyInfoBar/HandyInfoBar.vue'
import { initAssignIncidentOnPitch } from '@desktop/views/Relation/Sports/components/RelationView/Main/PitchWithIncidents/IncidentsOverlay/useAssignIncidentOnPitch'
import PitchIncidentsSeparator from '@desktop/views/Relation/Sports/components/RelationView/Main/PitchWithIncidents/PitchIncidents/PitchIncidentsSeparator.vue'
import PitchParticipantWithIncidents from '@desktop/views/Relation/Sports/components/RelationView/Main/PitchWithIncidents/PitchIncidents/PitchParticipantWithIncidents/PitchParticipantWithIncidents.vue'
import PitchWithChangePossession from '@desktop/views/Relation/Sports/components/RelationView/Main/PitchWithIncidents/PitchWithChangePossession/PitchWithChangePossession.vue'
import PitchWithIncidents from '@desktop/views/Relation/Sports/components/RelationView/Main/PitchWithIncidents/PitchWithIncidents.vue'

import TacklesMadeCounter from '../../../RugbyLeague/components/RelationView/Main/PitchWithIncidents/TacklesMadeCounter/TacklesMadeCounter.vue'
import PocketIncidents from '../PocketIncidents/PocketIncidents.vue'
import Pitch from './Pitch/Pitch.vue'

// composables
const { getWithCurrentTeamSides, getParticipantsWithCurrentTeamSides } =
  useTeamSides()
const { currentFirstParticipant, currentSecondParticipant } =
  getParticipantsWithCurrentTeamSides()
const { homeIncidentsTbd, awayIncidentsTbd } = useIncidentsToConfirm()
const [
  currentFirstParticipantIncidentsTbd,
  currentSecondParticipantIncidentsTbd,
] = getWithCurrentTeamSides(homeIncidentsTbd, awayIncidentsTbd)
const { event } = useEvent()

// computed
const showTacklesMadeCounter = computed(
  () => event.value.sport_id === SportId.RugbyLeague,
)

await initAssignIncidentOnPitch()
</script>

<template>
  <PitchWithIncidents
    class="@2md:grid-rows-[auto_auto_1fr] @2md:grid-cols-[9.25rem_auto_9.25rem] @2md:gap-x-2 grid-cols-[8.25rem_1fr_auto_1fr_8.25rem] grid-rows-[auto_auto_auto_1fr]"
    type="grid"
  >
    <PocketIncidents
      class="@2md:col-start-2 @2md:col-span-1 col-span-5 row-start-1"
    />
    <HandyInfoBar
      class="@2md:col-start-2 @2md:col-span-1 col-span-5 row-start-2"
    />
    <div class="@2md:row-start-1 col-start-1 row-start-3">
      <IncidentsToConfirm
        v-if="currentFirstParticipant"
        v-model="currentFirstParticipantIncidentsTbd"
        :participant="currentFirstParticipant"
      />
      <TacklesMadeCounter
        v-if="showTacklesMadeCounter && currentFirstParticipant"
        :participant="currentFirstParticipant"
      />
    </div>
    <PitchWithChangePossession
      class="@2md:col-start-2 @2md:col-span-1 @max-2md:px-1.5 col-span-3 col-start-2 row-start-3"
      changePossessionButtonSize="sm"
    >
      <Pitch />
    </PitchWithChangePossession>

    <div class="@2md:row-start-1 @2md:col-start-3 col-start-5 row-start-3">
      <IncidentsToConfirm
        v-if="currentSecondParticipant"
        v-model="currentSecondParticipantIncidentsTbd"
        :participant="currentSecondParticipant"
      />
      <TacklesMadeCounter
        v-if="showTacklesMadeCounter && currentSecondParticipant"
        :participant="currentSecondParticipant"
      />
    </div>

    <PitchParticipantWithIncidents
      v-if="currentFirstParticipant"
      class="@2md:row-start-2 @2md:col-start-1 @2md:row-span-2 @2md:col-span-1 col-span-2 col-start-1 row-start-4"
      incidentsClass="@2md:pr-1"
      :participant="currentFirstParticipant"
    />

    <PitchIncidentsSeparator class="@2md:hidden col-start-3 row-start-4" />

    <PitchParticipantWithIncidents
      v-if="currentSecondParticipant"
      class="@2md:row-start-2 @2md:col-start-3 @2md:row-span-2 @2md:col-span-1 col-span-2 col-start-4 row-start-4"
      incidentsClass="@2md:pl-1"
      :participant="currentSecondParticipant"
      reversed
    />
  </PitchWithIncidents>
</template>
